import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AsyncPipe, DatePipe, NgClass, CommonModule } from '@angular/common';
import { NotificationService } from '@core/services/notification.service';
import { Notification, TipoNotificacao } from '@shared/models/notification.model';
import { destinoNotificacaoRelatorio } from '@shared/utils/destino-notificacao-relatorio';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DropdownComponent } from '@shared/ui/dropdown/dropdown.component';
import { IconName } from '@core/constants/icons';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-notification-list',
  standalone: true,
  imports: [CommonModule, AsyncPipe, DatePipe, NgClass, IconComponent],
  templateUrl: './notification-list.component.html',
  styleUrl: './notification-list.component.scss',
})
export class NotificationListComponent {
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  /** Dropdown do sino (conteúdo projetado) — fechado ao navegar por uma notificação. */
  private readonly dropdown = inject(DropdownComponent, { optional: true });

  notifications$ = this.notificationService.notifications$.pipe(
    map((notifications) =>
      notifications.map((n) => ({
        ...n,
        titulo: this.getNotificationTitle(n),
        icon: this.getIconName(n),
        navegavel: this.ehNavegavel(n),
      }))
    )
  );

  /** Contagem vinda do servidor: a lista carregada é só a 1ª página e não serve de base para o badge. */
  unreadCount$ = this.notificationService.totalNaoLidas$;

  private getNotificationTitle(n: Notification): string {
    if (n.tipo === TipoNotificacao.RELATORIO_CONCLUIDO) return 'Relatório concluído';
    if (n.tipo === TipoNotificacao.RELATORIO_PROCESSANDO) return 'Relatório em processamento';
    if (n.tipo === TipoNotificacao.RELATORIO_FALHOU) return 'Relatório não gerado';
    if (n.tipo === TipoNotificacao.ALERTA) return 'Alerta';
    return 'Notificação do Sistema';
  }

  private getIconName(n: Notification): IconName {
    if (n.tipo === TipoNotificacao.ALERTA) return 'huge_alert_02';
    if (n.tipo === TipoNotificacao.RELATORIO_CONCLUIDO) return 'tick';
    if (n.tipo === TipoNotificacao.RELATORIO_FALHOU) return 'huge_alert_02';
    if (n.tipo === TipoNotificacao.RELATORIO_PROCESSANDO) return 'reload';
    return 'notifications';
  }

  /**
   * Uma notificação só é clicável quando traz um deep-link conhecido em `dados`.
   *
   * As duas rotas conhecidas terminam no detalhe da folha: `preenchimento` (assinatura/atraso) e
   * `nao-conformidade` (#789 — a NC aguarda a ação do responsável), que traz também
   * `naoConformidadeId` para cair direto na NC. Relatório pronto ou que falhou leva à tela de
   * relatórios (ou à liberação, quando o pedido é de fiscal).
   */
  private ehNavegavel(n: Notification): boolean {
    if (destinoNotificacaoRelatorio(n)) return true;
    const rota = n.dados?.rota;
    return (rota === 'preenchimento' || rota === 'nao-conformidade') && !!n.dados?.preenchimentoId;
  }

  /** Clique no card: marca como lida e, se houver deep-link, navega. */
  aoClicarNotificacao(n: Notification): void {
    this.markAsRead(n.id);
    if (!this.ehNavegavel(n)) return;

    this.dropdown?.closeDropdown();
    const destinoRelatorio = destinoNotificacaoRelatorio(n);
    if (destinoRelatorio) {
      void this.router.navigate(destinoRelatorio.comandos, { queryParams: destinoRelatorio.queryParams });
      return;
    }
    // A NC vive na aba de ocorrências do detalhe, sob a âncora `nc-<id>`: o parâmetro `nc` diz
    // à tela qual delas trazer para a viewport.
    const queryParams =
      n.dados.rota === 'nao-conformidade'
        ? { aba: 'ocorrencias', ...(n.dados.naoConformidadeId ? { nc: n.dados.naoConformidadeId } : {}) }
        : n.dados.aba
          ? { aba: n.dados.aba }
          : {};
    void this.router.navigate(['/preenchimentos', n.dados.preenchimentoId], { queryParams });
  }

  markAsRead(id: string): void {
    this.notificationService.markAsRead(id);
  }

  onLoadMore(): void {
    this.notificationService.loadMore();
  }

  onMarkAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  verTodas(): void {
    this.router.navigate(['/notificacoes']);
  }
}
